<script setup lang="ts">
    import { computed, onMounted, ref, watch } from "vue";
    import { defaultAutocompleteConverter } from "@/components/forms/inputs/Autocomplete/helpers";
    import { useStore } from "@/store";
    import { flashWarning } from "@/utils";
    import type { KallelsemallDisplay } from "@/store/modules/kallelsemallModule";

    interface Props {
        border?: boolean;
        variant?: "blue-background" | "transparent";
        label?: string;
    }

    withDefaults(defineProps<Props>(), {
        border: false,
        variant: "blue-background",
        label: "Kallelsemall",
    });

    const emit = defineEmits<{
        (
            event: "update:modelValue",
            payload: { value: KallelsemallDisplay | string | number | null; name: string }
        ): void;
    }>();

    const store = useStore();
    const activeSubsetName = ref<string>("default");
    const selectedValue = ref<unknown>(null);

    const fetchKallelsemallar = async ({ query }: { query?: string } = {}) => {
        try {
            const params: { q?: string } = {};

            if (query) {
                params.q = query;
            }

            const { subsetName } = await store.dispatch("kallelsemall/fetchKallelsemallar", {
                filterParams: params,
                displayMode: true,
            });

            activeSubsetName.value = subsetName;

            const options = store.getters["kallelsemall/kallelsemallSubset"]({
                subsetName,
                offset: 0,
                limit: 40,
                displayMode: true,
            }) as KallelsemallDisplay[];

            return options ?? [];
        } catch (error) {
            flashWarning("Kunde inte hämta kallelsemallar");
        }

        return [];
    };

    const reduce = (option: KallelsemallDisplay) => {
        return option.value;
    };

    const optionsVersion = ref(0);

    const options = computed(
        () =>
            store.getters["kallelsemall/kallelsemallSubset"]({
                subsetName: activeSubsetName.value,
                offset: 0,
                limit: 40,
                displayMode: true,
            }) as KallelsemallDisplay[]
    );

    watch(
        options,
        () => {
            optionsVersion.value += 1;
        },
        { deep: true }
    );

    onMounted(async () => {
        await fetchKallelsemallar();
    });

    const onInputValue = (payload: { value: unknown; name: string }) => {
        selectedValue.value = payload.value;
        emit("update:modelValue", {
            name: payload.name,
            value: payload.value as KallelsemallDisplay | string | number | null,
        });
    };
</script>

<template>
    <KAutocomplete
        :key="optionsVersion"
        name="kallelsemall"
        :label="label"
        :options="options"
        :on-search="fetchKallelsemallar"
        :converter="defaultAutocompleteConverter"
        :reduce="reduce"
        :class="[{ 'field-container': border, [variant]: border }]"
        :value="selectedValue"
        v-bind="$attrs"
        @inputValue="onInputValue"
    />
</template>

<style lang="scss" scoped>
    @import "@/components/forms/Form/styles/FieldBackgrounds.scss";

    .field-container {
        // Om ingen variant ar satt, anvands inget bakgrundsfarg
        @include field-container-mixin();
    }
</style>
